import type { BaseConfig } from '~/utils/configTypes'
import { configToQuery, normalizeConfig, type ProductKey } from '~/utils/configUrl'
import { decodeColumnConfigs } from '~/utils/columnConfigUrl'
import { getConstraints } from '~/config/furnitureConstraints'
import type { CatalogPreset } from '~/presets/catalogPresets'
import { getColorHex } from '~/presets/catalogPresets'
import type { ColorName } from '~/utils/colorDictionary'

export function buildCatalogPresetHref(preset: CatalogPreset, colorOverride?: ColorName) {
  if (preset.type === 'table') {
    const texture = colorOverride ?? preset.colors[0]
    return {
      pathname: '/configurator/table',
      query: {
        width: String(preset.dimensions.width),
        depth: String(preset.dimensions.depth),
        material: 'chipboard',
        texture,
      },
    } as const
  }

  if (preset.type === 'work-table') {
    const texture = colorOverride ?? preset.colors[0]
    return {
      pathname: '/configurator/work-table',
      query: {
        width: String(preset.dimensions.width),
        height: String(preset.dimensions.height),
        texture,
        ...(preset.drawers ? { drawers: 'true' } : {}),
      },
    } as const
  }

  const product = (
    preset.collection === 'new-collection' && preset.type === 'wardrobe'
      ? 'wardrobe-new-collection'
      : preset.type
  ) as ProductKey

  const C = getConstraints(product)
  const activeColor = colorOverride ?? preset.colors[0]

  const cfg: BaseConfig = {
    width: preset.dimensions.width,
    height: preset.dimensions.height,
    depth: preset.dimensions.depth,
    color: getColorHex(activeColor),
    columns: preset.columns ?? C.columns?.default,
    plintHeight: preset.plintHeight ?? C.dimensions.plintHeight?.default,
  }

  if (preset.wardrobeCfg) {
    cfg.wardrobeCfg = preset.wardrobeCfg
  }

  if (preset.rackCfg) {
    cfg.rackCfg = preset.rackCfg
  }

  if (preset.colCfg) {
    cfg.columnConfigurations = decodeColumnConfigs(preset.colCfg)
  }

  if (preset.openingType) {
    cfg.openingType = preset.openingType
  }

  if (preset.isMirrored) {
    cfg.isMirrored = true
  }

  const normalized = normalizeConfig(cfg, product, C)
  const query = configToQuery(normalized, product, C)

  return {
    pathname: `/configurator/${product}`,
    query,
  } as const
}
